import EditIcon from "@mui/icons-material/Edit";
import { Box, Chip, ChipProps, IconButton } from "@mui/material";
import ActionButton from "../../components/common/buttons/ActionButton";

import Grid from "@mui/material/Grid";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axios-instance";
import Fillter from "../../components/common/Fillter";
import MuiTable from "../../components/common/MuiTable";
import { useCountries } from "../../hooks/useCountries";
import {
  Column,
  getOutletStatusEnumFromString,
  OutletStatusEnum,
} from "../../utils/constants";
import { KeyValueObject } from "../../utils/models";
import { pathAPI } from "../../utils/pathApi";

interface Props {
  type: string;
}

const formatDateTime = (value?: string | null) => (
  <div>{value ? moment(value).format("YYYY/MM/DD HH:mm:ss") : "N/A"}</div>
);

const Salons = (props: Props) => {
  const { t } = useTranslation();
  var currentLang: any = localStorage.getItem("language");
  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const [{ data: countries = [], isLoading: loadingCountries }] =
    useCountries();
  var currentLanguage: any = localStorage.getItem("language");
  const countryData = countries?.map((item: any) => {
    return {
      key: item.code,
      value: item.name || item.i18n_names?.[currentLanguage] || item.code,
    };
  });

  const getDataTable = async () => {
    setLoading(true);
    let endpoint = pathAPI.outlet;
    if (props.type === "salon") {
      endpoint = pathAPI.salons;
    } else {
      endpoint = pathAPI.salonsRegistration;
    }
    await axiosInstance
      .get(endpoint)
      .then((response: any) => {
        if (response.data) {
          const updatedData = response.data.data.map((item: any) => ({
            ...item,
            action: item.id,
          }));
          setDataTable(updatedData);
        }
        setLoading(false);
      })
      .catch((error: any) => {
        console.log("==== get salon error: ", error);
        setDataTable([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getDataTable();
  }, [props.type]);

  const navigate = useNavigate();

  const header: Column[] = [
    {
      id: "id",
      label: `${t("common.table.id")}`,
      minWidth: 10,
      align: "center",
    },
    {
      id: "salon_name",
      label: `${t("common.table.salon_name")}`,
      minWidth: 250,
      align: "center",
    },
    {
      id: "location",
      label: `${t("common.table.location")}`,
      minWidth: 200,
      align: "center",
      format: (value, row) => {
        if (row?.display_address) {
          return <div>{row.display_address}</div>;
        }

        if (!value) return <div>N/A</div>;

        if (value.name) {
          return <div>{value.name}</div>;
        }

        try {
          const nameStr =
            value.i18n_names?.[currentLang] || value.i18n_names?.en || "N/A";
          return <div>{nameStr}</div>;
        } catch (error) {
          console.error("Error displaying location:", error);
          return <div>Error</div>;
        }
      },
    },
    {
      id: "owner_name",
      label: `${t("common.table.owner")}`,
      minWidth: 150,
      align: "center",
      format: (value) => (value ? value : "N/A"),
    },
    {
      id: "package_name",
      label: `${t("common.table.package")}`,
      minWidth: 100,
      align: "center",
      format: (value) => (value ? value : "N/A"),
    },
    {
      id: "status",
      label: `${t("common.table.status")}`,
      minWidth: 10,
      width: 30,
      align: "center",
      format: (value: any) => {
        return (
          <Chip
            size="small"
            variant="outlined"
            color={statusTransform(value)?.color ?? "default"}
            label={statusTransform(value)?.value ?? "-"}
          />
        );
      },
    },
    {
      id: "created",
      label: `${t("common.table.created_date")}`,
      minWidth: 170,
      align: "center",
      format: formatDateTime,
    },
    {
      id: "last_modified",
      label: `${t("common.table.last_modified_date")}`,
      minWidth: 170,
      align: "center",
      format: formatDateTime,
    },
    {
      id: "action",
      label: `${t("common.table.actions")}`,
      minWidth: 100,
      align: "center",
      format: (id: number) => {
        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
          >
            <Box
              size={"small"}
              component={IconButton}
              onClick={() => navigate(`${id}`)}
            >
              <EditIcon color="success" />
            </Box>
          </Box>
        );
      },
    },
  ];

  const statusTransform = (
    status: string
  ): { color: ChipProps["color"]; value: string } | undefined => {
    const outletStatus = getOutletStatusEnumFromString(status);
    switch (outletStatus) {
      case OutletStatusEnum.ACTIVE:
        return { color: "success", value: "Active" };
      case OutletStatusEnum.REJECTED:
        return { color: "default", value: "Rejected" };
      case OutletStatusEnum.REVIEWING:
        return { color: "info", value: "Reviewing" };
      case OutletStatusEnum.WAIT_FOR_REVIEW:
        return { color: "warning", value: "Wait for review" };

      default:
        break;
    }
  };

  const handleSearch = () => {
    setLoading(true);
    let endpoint = pathAPI.outlet;
    if (props.type === "salon") {
      endpoint = pathAPI.salons;
    } else {
      endpoint = pathAPI.salonsRegistration;
    }

    // Build query parameters for search and country filter
    const queryParams = [];
    if (searchKeyword) {
      queryParams.push(`search=${encodeURIComponent(searchKeyword)}`);
    }
    if (selectedCountry) {
      queryParams.push(`country=${encodeURIComponent(selectedCountry)}`);
    }

    const queryString =
      queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
    
    axiosInstance
      .get(`${endpoint}${queryString}`)
      .then((response: any) => {
        if (response.data && response.data.data) {
          const updatedData = response.data.data.map((item: any) => ({
            ...item,
            action: item.id,
          }));
          console.log("Updated data:", updatedData);
          setDataTable(updatedData);
        } else {
          setDataTable([]);
        }
      })
      .catch((error: any) => {
        console.log("==== search error: ", error);
        setDataTable([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOnChangeCountry = (event: KeyValueObject | null) => {
    if (event) {
      setSelectedCountry(event.key);
    } else {
      setSelectedCountry("");
    }
  };

  const handleChangeSearch = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchKeyword(e.target.value);
  };

  return (
    <Box>
      <Grid container spacing={2} className="mb-2 m-auto">
        <Grid size={{ xs: 12, md: 10, lg: 10 }}>
          <Fillter
            inputPlaceholder="Search by name"
            handleOnChangeDropDown={(value) => handleOnChangeCountry(value)}
            countryData={countryData}
            showCountryFilter={true}
            showSearchFilter={true}
            onChangeSearch={handleChangeSearch}
            handleClickBtn={handleSearch}
            showBtnSearch
          />
        </Grid>
        {props.type === "salon" ? (
          <Grid
            size={{ xs: 12, md: 2, lg: 2 }}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              height: 40,
              margin: "auto 0",
            }}
          >
            <ActionButton appSize="large" onClick={() => navigate("/salon/add")}>
              Add Salon
            </ActionButton>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
      <MuiTable header={header} data={dataTable} isLoading={loading}></MuiTable>
    </Box>
  );
};

export default Salons;
